import React, { useEffect, useState } from "react";
import EditableField from "../components/EditableField"; // adjust path as needed
import axios from "axios";

const WebsiteConfig = () => {
  const [websiteConfig, setWebsiteConfig] = useState(null);
  const [contactChannels, setContactChannels] = useState(null);

  // Individual editable fields state for websiteConfig
  // States for react_app_name
  const [reactAppName, setReactAppName] = useState("");
  const [tempReactAppName, setTempReactAppName] = useState("");
  const [editingReactAppName, setEditingReactAppName] = useState(false);

  // States for react_app_title
  const [reactAppTitle, setReactAppTitle] = useState("");
  const [tempReactAppTitle, setTempReactAppTitle] = useState("");
  const [editingReactAppTitle, setEditingReactAppTitle] = useState(false);

  // States for react_app_link
  const [reactAppLink, setReactAppLink] = useState("");
  const [tempReactAppLink, setTempReactAppLink] = useState("");
  const [editingReactAppLink, setEditingReactAppLink] = useState(false);

  // Editable fields from contactChannels.social and support
  const [phone, setPhone] = useState("");
  const [tempPhone, setTempPhone] = useState("");
  const [editingPhone, setEditingPhone] = useState(false);

  const [email, setEmail] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);

  const [whatsapp, setWhatsapp] = useState("");
  const [tempWhatsapp, setTempWhatsapp] = useState("");
  const [editingWhatsapp, setEditingWhatsapp] = useState(false);

  const [facebook, setFacebook] = useState("");
  const [tempFacebook, setTempFacebook] = useState("");
  const [editingFacebook, setEditingFacebook] = useState(false);

  const [instagram, setInstagram] = useState("");
  const [tempInstagram, setTempInstagram] = useState("");
  const [editingInstagram, setEditingInstagram] = useState(false);

  const [telegram, setTelegram] = useState("");
  const [tempTelegram, setTempTelegram] = useState("");
  const [editingTelegram, setEditingTelegram] = useState(false);

  const [youtube, setYoutube] = useState("");
  const [tempYoutube, setTempYoutube] = useState("");
  const [editingYoutube, setEditingYoutube] = useState(false);

  // States for X
  const [x, setX] = useState("");
  const [tempX, setTempX] = useState("");
  const [editingX, setEditingX] = useState(false);

  // States for Threads
  const [threads, setThreads] = useState("");
  const [tempThreads, setTempThreads] = useState("");
  const [editingThreads, setEditingThreads] = useState(false);

  // Helper: find social/support item by platform
  const getChannelValue = (platform, type = "social") => {
    if (!contactChannels) return "";
    const arr = contactChannels[type] || [];
    const item = arr.find((x) => x.platform === platform);
    return item ? item.value : "";
  };

  // Set individual contact fields from contactChannels on load
  useEffect(() => {
    if (contactChannels) {
      setPhone(getChannelValue("phone", "support"));
      setEmail(getChannelValue("email", "support"));
      setWhatsapp(getChannelValue("whatsapp", "social"));
      setFacebook(getChannelValue("facebook", "social"));
      setInstagram(getChannelValue("instagram", "social"));
      setTelegram(getChannelValue("telegram", "social"));
      setX(getChannelValue("x", "social"));
      setYoutube(getChannelValue("youtube", "social"));
      setThreads(getChannelValue("threads", "social"));
    }
  }, [contactChannels]);

  // Set websiteConfig fields on load
  useEffect(() => {
    if (websiteConfig) {
      setReactAppTitle(websiteConfig.react_app_title || "");
      setReactAppLink(websiteConfig.react_app_link || "");
      setReactAppName(websiteConfig.react_app_name || "");
    }
  }, [websiteConfig]);

  // Fetch initial data
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL_API}/admin/websiteconfig`)
      .then((res) => {
        if (res.data.success) {
          setWebsiteConfig(res.data.data.websiteConfig);
          setContactChannels(res.data.data.contactChannels);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch config:", err);
      });
  }, []);

  // Generic handlers for edit/cancel
  const handleEdit = (setEditing, setTemp, currentValue) => {
    setTemp(currentValue);
    setEditing(true);
  };

  const handleCancel = (setEditing, setTemp, currentValue) => {
    setTemp(currentValue);
    setEditing(false);
  };

  // Save websiteConfig update
  const saveWebsiteConfigField = async (
    key,
    newValue,
    setEditing,
    setTemp,
    setValue
  ) => {
    if (!websiteConfig) return;
    if (websiteConfig[key] === newValue) {
      setEditing(false);
      return;
    }
    try {
      const updatedConfig = { ...websiteConfig, [key]: newValue };
      // Include current contactChannels to avoid overwrite
      await axios.post(`${process.env.REACT_APP_BASE_URL_API}/admin/updatewebsiteconfig`, {
        websiteConfig: updatedConfig,
        contactChannels,
      });
      setWebsiteConfig(updatedConfig);
      setValue(newValue);
      setEditing(false);
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  // Save contactChannels update (social or support)
  const saveContactChannel = async (
    platform,
    newValue,
    type,
    setEditing,
    setTemp,
    setValue
  ) => {
    if (!contactChannels) return;

    // Find and update the platform in social/support array
    const updatedArr = (contactChannels[type] || []).map((item) =>
      item.platform === platform ? { ...item, value: newValue } : item
    );

    const updatedContactChannels = {
      ...contactChannels,
      [type]: updatedArr,
    };

    try {
      // Include current websiteConfig to avoid overwrite
      await axios.post(`${process.env.REACT_APP_BASE_URL_API}/admin/updatewebsiteconfig`, {
        websiteConfig,
        contactChannels: updatedContactChannels,
      });
      setContactChannels(updatedContactChannels);
      setValue(newValue);
      setEditing(false);
    } catch (err) {
      console.error("Save failed", err);
    }
  };

  return (
    <div className="gap grid rounded-lg">
      <div className="bg-white">
        {/* SEO & Content */}
        <p className="border-y bg-gray-50 px-3 py-3 text-base font-bold capitalize">
          SEO & Content
        </p>
        <div className="grid gap-5 px-3 py-3 sm:grid-cols-2">
          <EditableField
            label="React App Name"
            value={reactAppName}
            tempValue={tempReactAppName}
            editing={editingReactAppName}
            onEdit={() =>
              handleEdit(
                setEditingReactAppName,
                setTempReactAppName,
                reactAppName
              )
            }
            onCancel={() =>
              handleCancel(
                setEditingReactAppName,
                setTempReactAppName,
                reactAppName
              )
            }
            onSave={() =>
              saveWebsiteConfigField(
                "react_app_name",
                tempReactAppName,
                setEditingReactAppName,
                setTempReactAppName,
                setReactAppName
              )
            }
            onChange={(e) => setTempReactAppName(e.target.value)}
          />

          <EditableField
            label="React App Title"
            value={reactAppTitle}
            tempValue={tempReactAppTitle}
            editing={editingReactAppTitle}
            onEdit={() =>
              handleEdit(
                setEditingReactAppTitle,
                setTempReactAppTitle,
                reactAppTitle
              )
            }
            onCancel={() =>
              handleCancel(
                setEditingReactAppTitle,
                setTempReactAppTitle,
                reactAppTitle
              )
            }
            onSave={() =>
              saveWebsiteConfigField(
                "react_app_title",
                tempReactAppTitle,
                setEditingReactAppTitle,
                setTempReactAppTitle,
                setReactAppTitle
              )
            }
            onChange={(e) => setTempReactAppTitle(e.target.value)}
          />

          <EditableField
            label="React App Link"
            value={reactAppLink}
            tempValue={tempReactAppLink}
            editing={editingReactAppLink}
            onEdit={() =>
              handleEdit(
                setEditingReactAppLink,
                setTempReactAppLink,
                reactAppLink
              )
            }
            onCancel={() =>
              handleCancel(
                setEditingReactAppLink,
                setTempReactAppLink,
                reactAppLink
              )
            }
            onSave={() =>
              saveWebsiteConfigField(
                "react_app_link",
                tempReactAppLink,
                setEditingReactAppLink,
                setTempReactAppLink,
                setReactAppLink
              )
            }
            onChange={(e) => setTempReactAppLink(e.target.value)}
          />
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              App Logo
            </label>

            {/* Show the logo from fixed URL */}
            {websiteConfig?.react_app_logo && (
              <img
                src={websiteConfig.react_app_logo}
                alt="App Logo"
                className="h-16 w-16 object-contain border rounded"
              />
            )}

            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const reader = new FileReader();

                reader.onloadend = async () => {
                  const base64 = reader.result; // base64 image data

                  try {
                    const res = await axios.post(
                      `${process.env.REACT_APP_BASE_URL_API}/admin/upload-logo`,
                      { logo_img: base64 }
                    );

                    if (res.data.success) {
                      alert("Logo uploaded successfully");
                      // No need to update react_app_logo URL here
                      // Just rely on fixed URL from your DB for display
                    } else {
                      alert("Logo upload failed");
                    }
                  } catch (err) {
                    console.error("Logo upload failed", err);
                    alert("Logo upload failed");
                  }
                };

                reader.readAsDataURL(file); // triggers onloadend
              }}
            />
          </div>

        </div>

        {/* Contacts */}
        <p className="border-y bg-gray-50 px-3 py-3 text-base font-bold capitalize">
          Contacts
        </p>
        <div className="grid gap-5 px-3 py-3 sm:grid-cols-2">
          <EditableField
            label="Phone"
            value={phone}
            editing={editingPhone}
            tempValue={tempPhone}
            onEdit={() => handleEdit(setEditingPhone, setTempPhone, phone)}
            onCancel={() => handleCancel(setEditingPhone, setTempPhone, phone)}
            onChange={(e) => setTempPhone(e.target.value)}
            onSave={() =>
              saveContactChannel(
                "phone",
                tempPhone,
                "support",
                setEditingPhone,
                setTempPhone,
                setPhone
              )
            }
          />
          <EditableField
            label="Email"
            value={email}
            editing={editingEmail}
            tempValue={tempEmail}
            onEdit={() => handleEdit(setEditingEmail, setTempEmail, email)}
            onCancel={() => handleCancel(setEditingEmail, setTempEmail, email)}
            onChange={(e) => setTempEmail(e.target.value)}
            onSave={() =>
              saveContactChannel(
                "email", // platform
                tempEmail, // new value
                "support", // support array type
                setEditingEmail,
                setTempEmail,
                setEmail
              )
            }
          />
        </div>

        {/* Socials */}
        <p className="border-y bg-gray-50 px-3 py-3 text-base font-bold capitalize">
          Socials
        </p>
        <div className="grid gap-5 px-3 py-3 sm:grid-cols-2">
          <EditableField
            label="Whatsapp"
            value={whatsapp}
            editing={editingWhatsapp}
            tempValue={tempWhatsapp}
            onEdit={() =>
              handleEdit(setEditingWhatsapp, setTempWhatsapp, whatsapp)
            }
            onCancel={() =>
              handleCancel(setEditingWhatsapp, setTempWhatsapp, whatsapp)
            }
            onSave={() =>
              saveContactChannel(
                "whatsapp",
                tempWhatsapp,
                "social",
                setEditingWhatsapp,
                setTempWhatsapp,
                setWhatsapp
              )
            }
            onChange={(e) => setTempWhatsapp(e.target.value)}
          />

          <EditableField
            label="Facebook"
            value={facebook}
            editing={editingFacebook}
            tempValue={tempFacebook}
            onEdit={() =>
              handleEdit(setEditingFacebook, setTempFacebook, facebook)
            }
            onCancel={() =>
              handleCancel(setEditingFacebook, setTempFacebook, facebook)
            }
            onSave={() =>
              saveContactChannel(
                "facebook",
                tempFacebook,
                "social",
                setEditingFacebook,
                setTempFacebook,
                setFacebook
              )
            }
            onChange={(e) => setTempFacebook(e.target.value)}
          />

          <EditableField
            label="Instagram"
            value={instagram}
            editing={editingInstagram}
            tempValue={tempInstagram}
            onEdit={() =>
              handleEdit(setEditingInstagram, setTempInstagram, instagram)
            }
            onCancel={() =>
              handleCancel(setEditingInstagram, setTempInstagram, instagram)
            }
            onSave={() =>
              saveContactChannel(
                "instagram",
                tempInstagram,
                "social",
                setEditingInstagram,
                setTempInstagram,
                setInstagram
              )
            }
            onChange={(e) => setTempInstagram(e.target.value)}
          />

          <EditableField
            label="Telegram"
            value={telegram}
            editing={editingTelegram}
            tempValue={tempTelegram}
            onEdit={() =>
              handleEdit(setEditingTelegram, setTempTelegram, telegram)
            }
            onCancel={() =>
              handleCancel(setEditingTelegram, setTempTelegram, telegram)
            }
            onSave={() =>
              saveContactChannel(
                "telegram",
                tempTelegram,
                "social",
                setEditingTelegram,
                setTempTelegram,
                setTelegram
              )
            }
            onChange={(e) => setTempTelegram(e.target.value)}
          />

          <EditableField
            label="Youtube"
            value={youtube}
            editing={editingYoutube}
            tempValue={tempYoutube}
            onEdit={() =>
              handleEdit(setEditingYoutube, setTempYoutube, youtube)
            }
            onCancel={() =>
              handleCancel(setEditingYoutube, setTempYoutube, youtube)
            }
            onSave={() =>
              saveContactChannel(
                "youtube",
                tempYoutube,
                "social",
                setEditingYoutube,
                setTempYoutube,
                setYoutube
              )
            }
            onChange={(e) => setTempYoutube(e.target.value)}
          />

          <EditableField
            label="X"
            value={x}
            editing={editingX}
            tempValue={tempX}
            onEdit={() => handleEdit(setEditingX, setTempX, x)}
            onCancel={() => handleCancel(setEditingX, setTempX, x)}
            onSave={() =>
              saveContactChannel(
                "x",
                tempX,
                "social",
                setEditingX,
                setTempX,
                setX
              )
            }
            onChange={(e) => setTempX(e.target.value)}
          />

          <EditableField
            label="Threads"
            value={threads}
            editing={editingThreads}
            tempValue={tempThreads}
            onEdit={() =>
              handleEdit(setEditingThreads, setTempThreads, threads)
            }
            onCancel={() =>
              handleCancel(setEditingThreads, setTempThreads, threads)
            }
            onSave={() =>
              saveContactChannel(
                "threads",
                tempThreads,
                "social",
                setEditingThreads,
                setTempThreads,
                setThreads
              )
            }
            onChange={(e) => setTempThreads(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default WebsiteConfig;
